/**
 * ONE TEXT OR QUOTE BLOCK, IN THE EDITOR - the border between the block the API holds and the
 * document Tiptap edits (register R-002, and R-001's cure).
 *
 * ★★ EVERY CHANGE GOES OUT AS THE SERVER WOULD DERIVE IT. `@moonx/ui`'s `EditorShell` answers
 * ProseMirror JSON; `textFromEditorDoc` (shared/content-model/editor-json.ts) turns that into the
 * block's `body`, its paragraph rows and its marks - the rows from `paragraphRowsFor` over the new
 * body, the marks from `canonicaliseMarks` - which are the very functions the save checks a block
 * with (`article-editor.schema.ts`, `refuseParagraphRows` / `refuseMarkRows`). The plain-text box
 * this replaces kept the server's OLD rows beside a NEW body, and every save of a reopened draft
 * was refused `PARAGRAPH_ROWS_DISAGREE` (R-001, lens finding C2-01). Nothing here keeps a row the
 * editor did not just compute.
 *
 * ★ A BLOCK CHANGED FROM OUTSIDE IS RE-OPENED, NOT MERGED. The editor reads its document once, when
 * it mounts (EditorShell's header). So this remembers what the editor holds - the block it opened
 * with, then each change it reported - and when the block arrives different from that (the author
 * took the server's copy in a conflict, or a transition re-read the article), it mounts a fresh
 * editor over the new block. An echo of the editor's own change is equal and changes nothing, so
 * the cursor never jumps while the author types.
 *
 * ★ LINKS AND NESTING ARE THE PLATFORM'S RULES, handed in rather than copied: `LINK_HREF_RE` is the
 * database's `ck_awmark_href`, and `MAX_LIST_LEVEL` its `ck_awpara_level`. They and the converter
 * are imported from `shared/content-model` by relative path, as ./document.ts imports the id
 * generator - the package is not a dependency of this app (features/cabinet/api/editor-measure.ts
 * says why the path is deep).
 */
import { useMemo, useState } from 'react';
import { EditorShell } from '@moonx/ui';
import type { EditorShellProps } from '@moonx/ui';
import { LINK_HREF_RE, MAX_LIST_LEVEL } from '../../../../../../shared/content-model/blocks';
import {
  editorDocFromText,
  textFromEditorDoc,
} from '../../../../../../shared/content-model/editor-json';
import type { EditableText } from '../../../../../../shared/content-model/editor-json';
import type { QuoteBlock, TextBlock } from '../../../features/cabinet/api/editor-api';

type EditorDocument = NonNullable<EditorShellProps['initialContent']>;

export interface TextBlockEditorProps {
  readonly block: TextBlock | QuoteBlock;
  /** The accessible name of the text area - the block's own label, "Block 2, text". */
  readonly label: string;
  /** The toolbar's accessible name: "Formatting for block 2, text". */
  readonly toolbarLabel: string;
  readonly disabled: boolean;
  /** A sentence under the text, read with it - a quote over its ruled length. */
  readonly error?: string | null | undefined;
  /** The block's new `body`, `paragraphs` (`[]` for a quote) and `marks`. */
  readonly onChange: (text: EditableText) => void;
}

/** The members the editor owns, as the block carries them. */
export function membersOf(block: TextBlock | QuoteBlock): EditableText {
  return {
    body: block.body,
    paragraphs: block.kind === 'text' ? (block.paragraphs ?? []) : [],
    marks: (block.marks ?? []).map((mark) => ({
      startCp: mark.startCp,
      endCp: mark.endCp,
      kind: mark.kind,
      linkHref: mark.linkHref ?? null,
    })),
  };
}

function sameMembers(left: EditableText, right: EditableText): boolean {
  return (
    left.body === right.body &&
    left.paragraphs.length === right.paragraphs.length &&
    left.paragraphs.every((row, index) => {
      const other = right.paragraphs[index];
      return (
        other !== undefined &&
        row.paraIndex === other.paraIndex &&
        row.startCp === other.startCp &&
        row.endCp === other.endCp &&
        row.style === other.style &&
        row.listLevel === other.listLevel
      );
    }) &&
    left.marks.length === right.marks.length &&
    left.marks.every((mark, index) => {
      const other = right.marks[index];
      return (
        other !== undefined &&
        mark.startCp === other.startCp &&
        mark.endCp === other.endCp &&
        mark.kind === other.kind &&
        mark.linkHref === other.linkHref
      );
    })
  );
}

function isStoredLink(href: string): boolean {
  return LINK_HREF_RE.test(href);
}

export function TextBlockEditor({
  block,
  label,
  toolbarLabel,
  disabled,
  error,
  onChange,
}: TextBlockEditorProps) {
  /* What the mounted editor holds, and which mount it is. Kept in state rather than a ref so that
     the comparison below is a render-time derivation React can replay, StrictMode included. */
  const [held, setHeld] = useState(() => ({ text: membersOf(block), mount: 0 }));
  const incoming = membersOf(block);
  if (!sameMembers(incoming, held.text)) {
    // Changed from outside: re-open. Setting state while rendering is React's own pattern for
    // "adjust state when a prop changes"; it renders again at once, before anything is painted.
    setHeld({ text: incoming, mount: held.mount + 1 });
  }

  const kind = block.kind;
  // Built once per mount - the editor reads it only then (EditorShell's header) - so the mount is
  // its only dependency, on purpose: rebuilding it per keystroke would be work thrown away.
  const initialContent: EditorDocument = useMemo(
    () => editorDocFromText(kind, block.body, incoming.paragraphs, incoming.marks),
    [held.mount],
  );

  return (
    <EditorShell
      key={held.mount}
      label={label}
      toolbarLabel={toolbarLabel}
      initialContent={initialContent}
      paragraphStyles={kind === 'text'}
      isAllowedLink={isStoredLink}
      maxListLevel={MAX_LIST_LEVEL}
      disabled={disabled}
      error={error ?? undefined}
      onChange={(doc) => {
        const text = textFromEditorDoc(kind, doc);
        setHeld((current) => ({ text, mount: current.mount }));
        onChange(text);
      }}
    />
  );
}
